// src/ProtectedPages/ProtectedProgetti.js
import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { motion, AnimatePresence } from "framer-motion";
import Box from "@mui/material/Box";
import ImageList from "@mui/material/ImageList";
import ImageListItem from "@mui/material/ImageListItem";
import CircularProgress from "@mui/material/CircularProgress";
import pattern from "../Assets/Image/patternsvg.svg";
import ProjectModal from "../Components/Project/ProtectedProjectModal.js";
import { api } from "../config";
import AddProjectModal from "../Components/Forms/AddProjectModal.js";
import "../Pages/Styles/ProgettiStyles.css";
import Fab from "@mui/material/Fab";
import AddIcon from "@mui/icons-material/Add";
import { authFetch } from "../Pages/Utils/authFetch";
import DashboardBackButton from "../Components/Common/DashboardBackButton";

import {
    DndContext,
    PointerSensor,
    useSensor,
    useSensors,
    DragOverlay,
    closestCenter,
} from "@dnd-kit/core";
import {
    SortableContext,
    useSortable,
    arrayMove,
    rectSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const redGradient = "linear-gradient(90deg, #99191b 0%, #e3764c 100%)";

function getProjectImage(project) {
    if (Array.isArray(project.images) && project.images.length > 0) {
        const heroImage = project.images.find((img) => img.isHero) || project.images[0];
        const path = heroImage?.path || "";
        if (!path) return "/placeholder-image.jpg";
        if (path.includes(",")) return api(`/Progetti/${path}`);
        return api(`/Progetti/${project.id}/${path}`);
    }
    if (project.heroImage) {
        const path = project.heroImage;
        if (path.includes(",")) return api(`/Progetti/${path}`);
        return api(`/Progetti/${project.id}/${path}`);
    }
    return "/placeholder-image.jpg";
}

function SortableProjectItem({ project, onClick }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
        useSortable({ id: String(project.id) });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        cursor: "grab",
        opacity: isDragging ? 0.5 : 1,
    };

    return (
        <ImageListItem
            ref={setNodeRef}
            style={style}
            {...attributes}
            {...listeners}
            onClick={() => {
                if (isDragging) return;
                onClick(project);
            }}
            sx={{ cursor: "grab", overflow: "hidden" }}
        >
            <div className="masonry-image-wrapper">
                <img
                    src={getProjectImage(project)}
                    alt={project.titolo || project.citta}
                    loading="lazy"
                    style={{ width: "100%", display: "block" }}
                />
                <div className="masonry-image-overlay">
                    <div className="masonry-overlay-content">
                        <p className="masonry-project-title">{project.titolo}</p>
                        <p className="masonry-project-city">{project.citta}</p>
                    </div>
                </div>
            </div>
        </ImageListItem>
    );
}

export default function ProgettiHeaderResponsive() {
    const [projects, setProjects] = useState([]);
    const [displayedProjects, setDisplayedProjects] = useState([]);
    const [error, setError] = useState("");
    const [selectedProject, setSelectedProject] = useState(null);
    const [activeFilter, setActiveFilter] = useState("Tutti");
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [visibleCount, setVisibleCount] = useState(18);
    const [activeId, setActiveId] = useState(null);
    const [loading, setLoading] = useState(true);

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 6 } })
    );

    const filters = ["Tutti", "Sanità", "Scolastico", "PPP"];

    const getOrderedFilters = () => {
        if (activeFilter === "Tutti") return filters;
        return [
            activeFilter,
            ...filters.filter((f) => f !== activeFilter && f !== "Tutti"),
            "Tutti",
        ];
    };

    const fetchProjects = async (category = "Tutti") => {
        try {
            setLoading(true);

            let url = api("/getProjects.php");
            if (category && category !== "Tutti") {
                url += `?category=${encodeURIComponent(category)}`;
            }

            const res = await fetch(url, { method: "GET" });
            const text = await res.text();

            let data;
            try {
                data = JSON.parse(text);
            } catch (e) {
                console.error("Invalid JSON from getProjects.php:", e, text);
                setError("Risposta non valida dal server");
                setProjects([]);
                setDisplayedProjects([]);
                return;
            }

            if (!Array.isArray(data)) {
                console.error("Formato risposta inatteso:", data);
                setError("Formato risposta non valido");
                setProjects([]);
                setDisplayedProjects([]);
                return;
            }

            const normalized = data.map((p, idx) => {
                const stableId =
                    p.id ??
                    p.project_id ??
                    p.slug ??
                    `tmp-${idx}-${(p.titolo || p.overlayText || "untitled")}`;

                return {
                    id: stableId,
                    ...p,
                    titolo: p.titolo ?? p.overlayText ?? "",
                    descrizione: p.descrizione ?? p.description ?? "",
                    citta: p.citta ?? p.city ?? "",
                    inizio: p.inizio || p.data_inizio || "",
                    fine: p.fine || p.data_fine || "",
                    categorie: p.categorie || p.categorie_ids || [],
                    categorie_nomi: p.categorie_nomi || [],
                    is_important: p.is_important || p.isImportant || 0,
                    isImportant: Boolean(p.is_important || p.isImportant),
                    images: Array.isArray(p.images) ? p.images : [],
                    heroImage: p.heroImage ?? null,
                };
            });

            setError("");
            setProjects(normalized);
            setDisplayedProjects(normalized.slice(0, visibleCount));
        } catch (err) {
            console.error("Errore fetchProjects:", err);
            setError("Failed to fetch projects");
            setProjects([]);
            setDisplayedProjects([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchProjects();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        setDisplayedProjects(projects.slice(0, visibleCount));
    }, [projects, visibleCount]);

    const handleProjectClick = (project) => setSelectedProject(project);
    const closeModal = () => setSelectedProject(null);

    const handleFilterClick = (filter) => {
        setActiveFilter(filter);
        setVisibleCount(18);
        fetchProjects(filter);
    };

    const handleProjectUpdate = () => {
        fetchProjects(activeFilter);
    };

    const toggleAddModal = () => setIsAddModalOpen((prev) => !prev);

    const handleProjectAdded = () => {
        fetchProjects(activeFilter);
    };

    const loadMoreProjects = () => {
        setVisibleCount((prev) => prev + 18);
    };

    const hasMoreProjects = projects.length > displayedProjects.length;

    const persistOrder = async (ordered) => {
        try {
            const payload = {
                category: activeFilter,
                ids: ordered.map((p) => Number(p.id)),
            };

            const res = await authFetch(
                api("/updateProjectOrder.php"),
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(payload),
                }
            );

            const text = await res.text();
            let json;
            try {
                json = JSON.parse(text);
            } catch (e) {
                console.error("Invalid JSON from updateProjectOrder.php:", e, text);
                toast.error("Errore nel salvataggio dell'ordine (risposta non valida)");
                return;
            }

            if (!json.success) {
                console.error("Update order error:", json.error);
                toast.error(`Errore salvataggio ordine: ${json.error}`);
                return;
            }

            toast.success("Ordine salvato");
        } catch (e) {
            console.error("persistOrder error:", e);
            if (!String(e.message).includes("Unauthorized")) {
                toast.error("Errore nel salvataggio dell'ordine");
            }
        }
    };

    const activeProject = useMemo(
        () => displayedProjects.find((p) => String(p.id) === String(activeId)),
        [activeId, displayedProjects]
    );

    return (
        <>
            <div className="progetti-header-container">
                <DashboardBackButton
                    boxSx={{ position: 'absolute', left: 18, top: '50%', transform: 'translateY(-50%)', p: 0, zIndex: 10 }}
                    buttonSx={{
                        color: '#fff',
                        backgroundColor: 'rgba(255,255,255,0.12)',
                        border: '1px solid rgba(255,255,255,0.26)',
                        '&:hover': { backgroundColor: 'rgba(255,255,255,0.18)' },
                    }}
                />
                <div className="progetti-header-svg-wrapper">
                    <img
                        src={pattern}
                        alt="pattern"
                        className="progetti-header-svg-img"
                        draggable={false}
                    />
                </div>
                <div className="progetti-header-right-zone">
                    <div className="progetti-header-title-container">
                        <h1 className="progetti-header-title">PROGETTI</h1>
                    </div>
                    <div className="progetti-header-filters-wrapper">
                        <AnimatePresence mode="popLayout">
                            {getOrderedFilters().map((f) => (
                                <motion.button
                                    key={f}
                                    className="progetti-header-filter-btn"
                                    style={{ opacity: activeFilter === f ? 1 : 0.6 }}
                                    onClick={() => handleFilterClick(f)}
                                    layout
                                    initial={{ opacity: 0, scale: 0.8 }}
                                    animate={{
                                        opacity: activeFilter === f ? 1 : 0.6,
                                        scale: 1,
                                    }}
                                    exit={{ opacity: 0, scale: 0.8 }}
                                    transition={{
                                        type: "spring",
                                        stiffness: 500,
                                        damping: 30,
                                        duration: 0.3,
                                    }}
                                    whileHover={{ opacity: 1, scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                >
                                    {f}
                                </motion.button>
                            ))}
                        </AnimatePresence>
                    </div>
                </div>
            </div>

            <div className="projects-outer-container">
                {error && <div className="alert alert-danger">{error}</div>}

                <Box
                    sx={{
                        width: "100%",
                        height: "auto",
                        overflowY: "initial",
                        padding: "0 2rem",
                    }}
                >
                    {loading ? (
                        <Box sx={{ py: 10, display: "flex", justifyContent: "center" }}>
                            <CircularProgress color="error" />
                        </Box>
                    ) : (
                        <>
                            <DndContext
                                sensors={sensors}
                                collisionDetection={closestCenter}
                                onDragStart={(e) => setActiveId(e.active.id)}
                                onDragCancel={() => setActiveId(null)}
                                onDragEnd={(e) => {
                                    const { active, over } = e;
                                    setActiveId(null);
                                    if (!over || active.id === over.id) return;

                                    const oldIndex = displayedProjects.findIndex(
                                        (p) => String(p.id) === String(active.id)
                                    );
                                    const newIndex = displayedProjects.findIndex(
                                        (p) => String(p.id) === String(over.id)
                                    );
                                    if (oldIndex === -1 || newIndex === -1) return;

                                    const newDisplayed = arrayMove(
                                        displayedProjects,
                                        oldIndex,
                                        newIndex
                                    );
                                    setDisplayedProjects(newDisplayed);

                                    const idsDisplayedOld = displayedProjects.map((p) => p.id);
                                    const rest = projects.filter(
                                        (p) => !idsDisplayedOld.includes(p.id)
                                    );
                                    const merged = [...newDisplayed, ...rest];
                                    setProjects(merged);

                                    persistOrder(merged);
                                }}
                            >
                                <SortableContext
                                    items={displayedProjects.map((p) => String(p.id))}
                                    strategy={rectSortingStrategy}
                                >
                                    <ImageList
                                        variant="masonry"
                                        cols={3}
                                        gap={16}
                                        sx={{ margin: 0, padding: "2rem 0" }}
                                        key={`grid-${activeFilter}`}
                                    >
                                        {displayedProjects.map((project) => (
                                            <SortableProjectItem
                                                key={project.id}
                                                project={project}
                                                onClick={handleProjectClick}
                                            />
                                        ))}
                                    </ImageList>
                                </SortableContext>

                                <DragOverlay dropAnimation={null}>
                                    {activeProject ? (
                                        <div style={{ pointerEvents: "none" }}>
                                            <img
                                                src={getProjectImage(activeProject)}
                                                alt=""
                                                style={{ width: 220, borderRadius: 10 }}
                                            />
                                        </div>
                                    ) : null}
                                </DragOverlay>
                            </DndContext>

                            {hasMoreProjects && (
                                <div className="projects-load-more-container">
                                    <button
                                        className="projects-load-more-btn"
                                        onClick={loadMoreProjects}
                                    >
                                        Carica Altri Progetti
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </Box>
            </div>

            {selectedProject && (
                <ProjectModal
                    project={selectedProject}
                    isOpen={!!selectedProject}
                    onClose={closeModal}
                    onUpdate={handleProjectUpdate}
                />
            )}

            <Fab
                color="primary"
                aria-label="add"
                onClick={toggleAddModal}
                sx={{
                    position: "fixed",
                    bottom: 24,
                    right: 24,
                    background: redGradient,
                }}
            >
                <AddIcon />
            </Fab>

            <AddProjectModal
                isOpen={isAddModalOpen}
                onClose={toggleAddModal}
                onProjectAdded={handleProjectAdded}
            />
        </>
    );
}
